"use client";
import { useState } from "react";
import { useCategories } from "@/hooks/useCategories";
import type { TransactionWithCategory } from "@/types";
import {
  isUncategorizedCategoryName,
  isUncategorizedTransaction,
} from "./uncategorized";

interface CategorySelectProps {
  transaction: TransactionWithCategory;
  onUpdate: (id: string, categoryId: string | null) => Promise<void>;
}

export function CategorySelect({ transaction, onUpdate }: CategorySelectProps) {
  const { categories } = useCategories();
  const [loading, setLoading] = useState(false);
  const uncategorized = isUncategorizedTransaction(transaction);
  const selectValue = uncategorized ? "" : (transaction.categoryId ?? "");
  const categoryOptionsBase = categories.filter(
    (category) => !isUncategorizedCategoryName(category.name),
  );
  const hasSelectedOption =
    selectValue === "" ||
    categoryOptionsBase.some((category) => category.id === selectValue);
  const categoryOptions =
    !hasSelectedOption && transaction.category && selectValue !== ""
      ? [
          {
            id: transaction.category.id,
            name: transaction.category.name,
            color: transaction.category.color,
            type: transaction.category.type,
          },
          ...categoryOptionsBase,
        ]
      : categoryOptionsBase;

  async function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const raw = e.target.value;
    const categoryId = raw === "" ? null : raw;
    const prev = uncategorized ? null : (transaction.categoryId ?? null);
    if (categoryId === prev) return;
    setLoading(true);
    try {
      await onUpdate(transaction.id, categoryId);
    } finally {
      setLoading(false);
    }
  }

  const current = transaction.category;

  return (
    <div className="flex items-center gap-2">
      {current && (
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: current.color }}
        />
      )}
      <select
        value={selectValue}
        onChange={handleChange}
        disabled={loading}
        className={`rounded border border-transparent bg-transparent px-1 py-0.5 text-sm text-[#1f2f45] focus:border-slate-300 focus:outline-none hover:border-slate-200 ${loading ? "opacity-50" : ""}`}
      >
        <option value="">Uncategorized</option>
        {categoryOptions.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}
